import { motion } from "framer-motion";
import { ArrowUpRight, Building2, Cpu, Factory, PlugZap, Sprout } from "lucide-react";

const EASE = [0.33, 1, 0.68, 1];

const SECTORS = [
  {
    id: "tech",
    num: "01",
    icon: Cpu,
    title: "Tech & App Architecture",
    tagline: "Scalable digital infrastructure.",
    body: "We design, build, and deploy high-performance mobile applications and enterprise software. From sleek consumer apps to complex backend ecosystems, our development pipeline transforms ambitious ideas into seamless, secure digital realities.",
    cta: "View Current Pipelines",
    type: "App Development",
    span: "lg:col-span-4",
    featured: true,
    image: "/images/tech.png",
  },
  {
    id: "manufacturing",
    num: "02",
    icon: Factory,
    title: "Advanced Manufacturing",
    tagline: "Precision-engineered hardware and automation.",
    body: "Bridging the gap between software and physical production. We leverage automation, robotics, and advanced materials to manufacture the hardware, components, and machinery required to power modern industries.",
    cta: "View Manufacturing Capability",
    type: "Advanced Manufacturing",
    span: "lg:col-span-4",
    image: "/images/manufacturing.png",
  },
  {
    id: "energy",
    num: "03",
    icon: PlugZap,
    title: "Clean Energy Systems",
    tagline: "Next-generation sustainable power grids.",
    body: "Powering the future responsibly. We design and implement clean energy technologies, microgrids, and renewable power infrastructure engineered to sustain large-scale industrial and residential operations.",
    cta: "Explore Energy Tech",
    type: "Clean Energy",
    span: "lg:col-span-4",
    image: "/images/energy.png",
  },
  {
    id: "agri",
    num: "04",
    icon: Sprout,
    title: "Next-Gen Agriculture",
    tagline: "Smart food supply systems.",
    body: "Revolutionizing traditional farming through automation, data-driven crop management, and climate-smart agricultural practices. We are building the blueprints for sustainable food security that scales globally.",
    cta: "Explore Agri-Tech",
    type: "Smart Agriculture",
    span: "lg:col-span-6",
    image: "/images/agri.png",
  },
  {
    id: "realestate",
    num: "05",
    icon: Building2,
    title: "Intelligent Real Estate",
    tagline: "Sustainable physical spaces.",
    body: "Conceptualizing and developing the smart spaces of tomorrow. Our focus is on high-yielding, modern residential and commercial properties engineered for an interconnected world.",
    cta: "Explore Real Estate",
    type: "Real Estate",
    span: "lg:col-span-6",
    image: "/images/realestate.png",
  },
];

const SectorCard = ({ sector, index, onContact }) => {
  const Icon = sector.icon;
  return (
    <motion.article
      initial={{ opacity: 0, y: 48 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.85, delay: index * 0.1, ease: EASE }}
      className={`group relative flex flex-col justify-between overflow-hidden border bg-surface p-8 transition-colors duration-500 hover:border-cyber/60 md:p-10 ${
        sector.featured ? "border-cyber/40" : "border-white/10"
      } ${sector.span}`}
      data-testid={`sector-card-${sector.id}`}
    >
      <div className="pointer-events-none absolute inset-0 bg-blueprint-fine opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
      <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-cyber/0 blur-3xl transition-all duration-700 group-hover:bg-cyber/10" />

      <div className="relative -mx-8 -mt-8 mb-8 h-44 overflow-hidden border-b border-white/10 md:-mx-10 md:-mt-10">
        <img
          src={sector.image}
          alt={sector.title}
          loading="lazy"
          className="h-full w-full object-cover opacity-70 transition-all duration-700 group-hover:scale-[1.05] group-hover:opacity-100"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-surface via-surface/20 to-transparent" />
      </div>

      <div className="relative">
        <div className="flex items-start justify-between">
          <span className="font-mono text-sm tracking-[0.3em] text-cyber">{sector.num} /</span>
          <Icon className="h-6 w-6 text-white/25 transition-colors duration-500 group-hover:text-neon" strokeWidth={1.25} />
        </div>
        {sector.featured && (
          <p className="mt-6 font-mono text-[9px] uppercase tracking-[0.35em] text-cyber" data-testid="sector-flagship-tag">
            Flagship Division
          </p>
        )}
        <h3
          className={`font-display font-medium tracking-tight ${
            sector.featured ? "mt-2 text-2xl md:text-3xl" : "mt-6 text-2xl md:text-3xl"
          }`}
          data-testid={`sector-title-${sector.id}`}
        >
          {sector.title}
        </h3>
        <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.25em] text-neon">
          {sector.tagline}
        </p>
        <p className="mt-6 text-sm leading-relaxed text-secondary">
          {sector.body}
        </p>
      </div>

      <button
        data-testid={`sector-cta-${sector.id}`}
        onClick={() => onContact(sector.type)}
        className="relative mt-12 flex w-fit items-center gap-2 border-b border-white/20 pb-1 font-mono text-[11px] uppercase tracking-[0.25em] text-white/70 transition-colors duration-300 hover:border-cyber hover:text-cyber"
      >
        {sector.cta}
        <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
      </button>
    </motion.article>
  );
};

const Sectors = ({ onContact }) => {
  return (
    <section
      id="sectors"
      data-testid="sectors-section"
      className="relative border-t border-white/10 bg-abyss py-32 md:py-44"
    >
      <div className="mx-auto max-w-[1600px] px-6 md:px-12">
        <div className="mb-20 grid grid-cols-1 gap-8 lg:grid-cols-12">
          <motion.p
            initial={{ opacity: 0, x: -24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: EASE }}
            className="font-mono text-xs uppercase tracking-[0.35em] text-neon md:text-sm lg:col-span-5"
            data-testid="sectors-overline"
          >
            // CORE OPERATING DIVISIONS
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.1, ease: EASE }}
            className="font-display text-3xl font-medium tracking-tight sm:text-4xl md:text-5xl lg:col-span-7"
            data-testid="sectors-headline"
          >
            Five divisions. <span className="text-white/40">One operating system for the future.</span>
          </motion.h2>
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-12">
          {SECTORS.map((sector, i) => (
            <SectorCard key={sector.id} sector={sector} index={i} onContact={onContact} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Sectors;
